/**
 * BLCameraDialog — modale plein écran pour photographier un Bon de Livraison
 * directement depuis l'application (web + mobile).
 *
 * Usage :
 *   <BLCameraDialog
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onCapture={(blob) => uploadBlob(blob)}
 *   />
 *
 * Comportement :
 * - getUserMedia rear camera (`facingMode: environment`)
 * - Bouton « Capturer » → toBlob jpeg 0.85 → onCapture(blob)
 * - Bouton « Reprendre » et « Confirmer » après capture
 * - Permet de prendre plusieurs photos d'affilée (BL multi-pages)
 */
import React, { useEffect, useRef, useState } from 'react';
import { Camera, X, RotateCcw, Check, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

const BLCameraDialog = ({ open, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [shotsCount, setShotsCount] = useState(0);

  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      try {
        const ms = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (!active) {
          ms.getTracks().forEach((t) => t.stop());
          return;
        }
        setStream(ms);
        setCameraError(null);
        if (videoRef.current) {
          videoRef.current.srcObject = ms;
        }
      } catch (e) {
        setCameraError(
          e.name === 'NotAllowedError'
            ? 'Accès caméra refusé. Autorisez-la dans les paramètres du navigateur.'
            : 'Caméra indisponible. Utilisez « Importer photos » à la place.'
        );
      }
    })();
    return () => {
      active = false;
    };
  }, [open]);

  // Stop tracks on close / unmount
  useEffect(() => {
    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [stream]);

  useEffect(() => {
    if (!open && stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
      setPreviewUrl(null);
      setPendingBlob(null);
      setShotsCount(0);
    }
  }, [open, stream]);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current || !stream) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth || 1280;
    c.height = v.videoHeight || 720;
    const ctx = c.getContext('2d');
    ctx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob(
      (blob) => {
        if (!blob) {
          toast.error('Erreur capture photo');
          return;
        }
        setPendingBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
      },
      'image/jpeg',
      0.85
    );
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPendingBlob(null);
  };

  const confirm = async () => {
    if (!pendingBlob || !onCapture) return;
    setCapturing(true);
    try {
      await onCapture(pendingBlob);
      setShotsCount((n) => n + 1);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setPendingBlob(null);
      toast.success('Photo ajoutée — vous pouvez en prendre une autre ou fermer');
    } catch (e) {
      toast.error('Erreur ajout photo');
    } finally {
      setCapturing(false);
    }
  };

  const closeDialog = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/95 flex flex-col"
      data-testid="bl-camera-dialog"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <Camera className="w-5 h-5" />
          <span className="font-semibold">Photographier le Bon de Livraison</span>
          {shotsCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 ml-2">
              {shotsCount} photo(s) ajoutée(s)
            </span>
          )}
        </div>
        <button
          onClick={closeDialog}
          className="p-2 hover:bg-white/10 rounded-full"
          data-testid="bl-camera-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 relative flex items-center justify-center bg-black overflow-hidden">
        {cameraError ? (
          <div className="text-center text-white/80 px-6 max-w-sm">
            <Camera className="w-12 h-12 mx-auto mb-3 text-red-400" />
            <p className="text-sm">{cameraError}</p>
          </div>
        ) : previewUrl ? (
          <img
            src={previewUrl}
            alt="Aperçu BL"
            className="max-h-full max-w-full object-contain"
            data-testid="bl-camera-preview"
          />
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="max-h-full max-w-full object-contain"
            data-testid="bl-camera-video"
          />
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Footer controls */}
      <div className="px-4 py-4 bg-black/60 backdrop-blur-sm">
        {cameraError ? (
          <Button
            onClick={closeDialog}
            variant="outline"
            className="w-full bg-white/10 text-white border-white/30 hover:bg-white/20"
          >
            Fermer
          </Button>
        ) : previewUrl ? (
          <div className="flex gap-2 max-w-md mx-auto">
            <Button
              onClick={retake}
              variant="outline"
              className="flex-1 bg-white/10 text-white border-white/30 hover:bg-white/20"
              data-testid="bl-camera-retake"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Reprendre
            </Button>
            <Button
              onClick={confirm}
              disabled={capturing}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bl-camera-confirm"
            >
              {capturing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              {capturing ? 'Ajout…' : 'Confirmer'}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={capturePhoto}
              disabled={!stream}
              className="w-16 h-16 rounded-full bg-white border-4 border-white/40 active:scale-95 transition disabled:opacity-50"
              data-testid="bl-camera-shutter"
              aria-label="Prendre la photo"
            />
            <p className="text-xs text-white/60">Cadrez le BL puis appuyez sur le bouton blanc</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BLCameraDialog;
